import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, HostBinding, EventEmitter, Input, Output, HostListener, ContentChild, InjectionToken, Injectable, Inject, ContentChildren, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

/**
 * Default Accessibility Configurations
 */
const accessibilityConfig = {
    // Accessibility Tab or Focus Tab Default Config
    focusTab: {
        borderColor: '-webkit-focus-ring-color',
        borderSize: 1,
        borderRadius: 5
    },
};

/**
 * Sometimes we want to apply focus only on the parent element
 * this directive will help target which button should be clicked on enter.
 */
class FocusTabButtonDirective {
    get getClass() {
        return {
            'focus-tab-btn': true
        };
    }
    constructor(_el) {
        this._el = _el;
    }
    click() {
        this._el.nativeElement.click();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FocusTabButtonDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: FocusTabButtonDirective, selector: "[focusTabBtn]", host: { properties: { "class": "this.getClass" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FocusTabButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[focusTabBtn]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { getClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * Some elements are not focusable like divs.etc, this directive will help focus the element
 * when tabbing and perform click on enter, default tabindex is set to 0 so it won't mess with
 * accessibility.
 */
class FocusTabDirective {
    get tabIndex() {
        return 0;
    }
    get getStyle() {
        return {
            'outline': !this._isCustom() && this.isFocused ? `-webkit-focus-ring-color auto 1px !important` : 'none',
            'border': this._isCustom() && this.isFocused ? `${this.tabBorderSize}px solid ${this.tabBorderColor}` : 'inherit',
            'border-radius': this._isCustom() && this.isFocused ? `${this.tabBorderRadius}px` : 'inherit'
        };
    }
    get getClass() {
        return {
            'focus-tab': true,
            'tab-focused': this.isFocused,
            'focus-tab-custom': this._isCustom() && this.isFocused
        };
    }
    onFocus(event) {
        this.isFocused = true;
    }
    onBlur(event) {
        this.isFocused = false;
    }
    onKeyupEnter(event) {
        if (this.allowClickOnEnter) {
            if (!this._tabElements.includes(this._el.nativeElement.tagName.toLowerCase())) {
                if (this.tabBtn) {
                    this.tabBtn.click();
                }
                else if (this._isNgbDropdown(this._el.nativeElement)) {
                    const dropdownButton = this._getNgbdropdownButton(this._el.nativeElement);
                    dropdownButton.click();
                }
                else {
                    this._el.nativeElement.click();
                }
            }
        }
        else {
            this.focusTabEnter.emit();
        }
    }
    constructor(_el) {
        this._el = _el;
        this.tabBorderColor = '-webkit-focus-ring-color';
        this.tabBorderSize = 1;
        this.tabBorderRadius = 5;
        this.allowClickOnEnter = true;
        this.focusTabEnter = new EventEmitter();
        this._tabElements = ['button', 'a', 'textarea', 'input', 'select'];
        this.isFocused = false;
    }
    _isNgbDropdown(el) {
        return el.hasAttribute('ngbdropdown');
    }
    _getNgbdropdownButton(el) {
        return el.children[0];
    }
    _isCustom() {
        return this.tabBorderColor != '-webkit-focus-ring-color';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FocusTabDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: FocusTabDirective, selector: "[focusTab]", inputs: { tabBorderColor: "tabBorderColor", tabBorderSize: "tabBorderSize", tabBorderRadius: "tabBorderRadius", allowClickOnEnter: "allowClickOnEnter" }, outputs: { focusTabEnter: "focusTabEnter" }, host: { listeners: { "focus": "onFocus($event)", "blur": "onBlur($event)", "keyup.enter": "onKeyupEnter($event)" }, properties: { "attr.tabindex": "this.tabIndex", "style": "this.getStyle", "class": "this.getClass" } }, queries: [{ propertyName: "tabBtn", first: true, predicate: FocusTabButtonDirective, descendants: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FocusTabDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[focusTab]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { tabBorderColor: [{
                type: Input
            }], tabBorderSize: [{
                type: Input
            }], tabBorderRadius: [{
                type: Input
            }], allowClickOnEnter: [{
                type: Input
            }], focusTabEnter: [{
                type: Output
            }], tabIndex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], getStyle: [{
                type: HostBinding,
                args: ['style']
            }], getClass: [{
                type: HostBinding,
                args: ['class']
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], onKeyupEnter: [{
                type: HostListener,
                args: ['keyup.enter', ['$event']]
            }], tabBtn: [{
                type: ContentChild,
                args: [FocusTabButtonDirective]
            }] } });

const ACCESSIBILITY_CONFIG = new InjectionToken('accessibilityConfig');
/**
 * We might want to change accessibility configurations in the form.etc in the future according to user
 * preference, this service will help.
 */
class AccessibilityService {
    constructor(_config) {
        this._config = _config;
        this._configSubject$ = new BehaviorSubject(this._config);
    }
    /**
     * Set accessibility configurations
     * @param {AccessibilityConfig} config
     */
    setConfig(config) {
        let conf = {
            focusTab: {
                ...this._configSubject$.getValue().focusTab, ...config.focusTab
            }
        };
        this._configSubject$.next(conf);
    }
    /**
     * Restore accessibility configurations to default
     */
    restoreDefaults() {
        this._configSubject$.next(this._config);
    }
    /**
     * Get the current accessibility configuration
     * @returns acessibility configuration
     */
    getConfig$() {
        return this._configSubject$.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityService, deps: [{ token: ACCESSIBILITY_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ACCESSIBILITY_CONFIG]
                }] }] });

/**
 * If we want to change accessibility configuration via service or module
 * we can append this directive to the parent element.
 */
class AccessibilityDirective {
    constructor(accessibilityService, _cd) {
        this.accessibilityService = accessibilityService;
        this._cd = _cd;
    }
    ngAfterContentInit() {
        this._subscription = this.accessibilityService.getConfig$()
            .subscribe((config) => {
            const { borderColor, borderSize, borderRadius } = config.focusTab;
            this._setFocusTabStyle(borderColor, borderSize, borderRadius);
            this._cd.markForCheck();
        });
    }
    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
    _setFocusTabStyle(color, size, radius) {
        this.focusTabs.forEach(tab => {
            tab.tabBorderColor = color;
            tab.tabBorderSize = size;
            tab.tabBorderRadius = radius;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityDirective, deps: [{ token: AccessibilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: AccessibilityDirective, selector: "[accessibility]", queries: [{ propertyName: "focusTabs", predicate: FocusTabDirective, descendants: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[accessibility]'
                }]
        }], ctorParameters: () => [{ type: AccessibilityService }, { type: i0.ChangeDetectorRef }], propDecorators: { focusTabs: [{
                type: ContentChildren,
                args: [FocusTabDirective, { descendants: true }]
            }] } });

class AccessibilityModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityModule, declarations: [AccessibilityDirective,
            FocusTabDirective,
            FocusTabButtonDirective], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule], exports: [AccessibilityDirective,
            FocusTabDirective,
            FocusTabButtonDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityModule, providers: [
            AccessibilityService,
            {
                provide: ACCESSIBILITY_CONFIG,
                useValue: accessibilityConfig
            }
        ], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AccessibilityDirective,
                        FocusTabDirective,
                        FocusTabButtonDirective
                    ],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule
                    ],
                    providers: [
                        AccessibilityService,
                        {
                            provide: ACCESSIBILITY_CONFIG,
                            useValue: accessibilityConfig
                        }
                    ],
                    exports: [
                        AccessibilityDirective,
                        FocusTabDirective,
                        FocusTabButtonDirective
                    ]
                }]
        }] });

class AccessibilityConfigModule {
    static forRoot(config) {
        return {
            ngModule: AccessibilityModule,
            providers: [
                {
                    provide: ACCESSIBILITY_CONFIG,
                    useValue: config
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityConfigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityConfigModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityConfigModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AccessibilityConfigModule, decorators: [{
            type: NgModule
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ACCESSIBILITY_CONFIG, AccessibilityConfigModule, AccessibilityDirective, AccessibilityModule, AccessibilityService, FocusTabButtonDirective, FocusTabDirective };
